import { Avatar } from '@askable/ui/components/avatar';
import { AskableIcon } from '@askable/ui/core/askable-icon';
import { AnchorButton, Button } from '@askable/ui/core/button';
import {
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuPortal,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from '@askable/ui/core/dropdown-menu';
import { cn } from '@askable/ui/lib/utils';
import { Check, ChevronDown, Users } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'src/lib/routing';
import { DataTestId, useMediaQuery } from 'shared-utils';

import { useAppContext } from 'components/common/Askable/Providers/appProvider';
import { globalVariables } from 'lib/globalVariables';
import { useIsGuest } from 'src/hooks/useIsGuest';
import { isImpersonating } from 'utils/impersonation';

import { ResponsiveMenu } from './ResponsiveMenu';

import type { ClientDetails } from 'context/ConnectedClientContext';

export const TeamSelector = ({ details }: { details: ClientDetails }) => {
  const { t } = useTranslation();
  const appContext = useAppContext();
  const connectedAsClient = useMemo(() => isImpersonating(), []);
  const navigate = useNavigate();
  const [isMounted, setIsMounted] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const isMobile = useMediaQuery('(max-width: 639px)');

  useEffect(() => {
    setIsMounted(true);
  }, []);

  const handleSwitchTeams = async (id: string) => {
    await appContext.onSwitchToDifferentTeam(id);
  };

  const handleSignOut = () => {
    navigate('/logout', { replace: true });
  };

  const isResearcher = details?.type?.researcher;
  const isGuest = useIsGuest();
  // Staff hop between client teams, so a client org's single-team rule shouldn't lock them in
  const hideSignIntoAnotherTeam =
    !details?.isAskableInternalStaff &&
    (details?.ConnectedTeam?.Organisation?.settings?.disallow_multiple_team_sign_on ?? false);
  const teams = details?.Teams?.filter((team): team is NonNullable<typeof team> => Boolean(team?._id)) ?? [];
  const hasTeams = teams.length > 0;

  const desktopContent = (
    <>
      <DropdownMenuLabel className="flex items-center gap-2 text-sm text-foreground">
        <Avatar name={details?.name?.firstname ?? undefined} size="sm" colorSeed={details?.id ?? undefined} />
        <span data-testid={DataTestId.userName}>{details?.name?.firstname}</span>
      </DropdownMenuLabel>

      <DropdownMenuSeparator />

      {!isResearcher ? (
        <>
          <DropdownMenuItem asChild>
            <Link to="/settings/account">{t('sections.settings.title')}</Link>
          </DropdownMenuItem>
          <DropdownMenuSeparator />
        </>
      ) : null}

      {hasTeams ? (
        <DropdownMenuSub>
          <DropdownMenuSubTrigger>{t('components.teamSwitcher.switchTeam')}</DropdownMenuSubTrigger>
          <DropdownMenuPortal>
            <DropdownMenuSubContent sideOffset={4} alignOffset={-4}>
              <DropdownMenuGroup className="flex max-h-[70vh] w-64 flex-col gap-[2px] overflow-y-auto">
                {teams.map(team => {
                  const isActiveTeam = isResearcher
                    ? team._id === details?.team?.id
                    : team._id === details?.ConnectedTeam?._id;

                  return (
                    <DropdownMenuItem
                      key={team._id}
                      onClick={() => void handleSwitchTeams(team._id)}
                      className={cn({
                        'items-start bg-secondary font-medium': isActiveTeam,
                      })}
                    >
                      <div className="w-4 shrink-0 pt-[1px]">{isActiveTeam ? <Check className="size-4" /> : null}</div>
                      {team.name}
                    </DropdownMenuItem>
                  );
                })}
              </DropdownMenuGroup>

              {!hideSignIntoAnotherTeam && !isResearcher ? (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to="/teams">
                      <Users className="size-4" />
                      {t('components.teamSwitcher.signInToAnotherTeam')}
                    </Link>
                  </DropdownMenuItem>
                </>
              ) : null}
            </DropdownMenuSubContent>
          </DropdownMenuPortal>
        </DropdownMenuSub>
      ) : null}

      {!isResearcher && !isGuest ? (
        <DropdownMenuItem
          onClick={() => {
            setIsOpen(false);
            appContext.onOpenInviteTeamMemberModal(null);
          }}
        >
          {t('sections.navigation.invite')}
        </DropdownMenuItem>
      ) : null}

      {globalVariables.getEnvironmentVariables().isDemo && (
        <DropdownMenuItem onClick={() => navigate('/reset-demos')}>
          {t('sections.navigation.resetDemoData')}
        </DropdownMenuItem>
      )}

      <DropdownMenuSeparator />
      <DropdownMenuItem onClick={handleSignOut}>{t('global.signOut')}</DropdownMenuItem>
    </>
  );

  const mobileContent = (
    <div className="flex flex-col">
      <div className="flex items-center gap-2 px-3 pb-4 text-sm">
        <Avatar name={details?.name?.firstname ?? undefined} size="sm" colorSeed={details?.id ?? undefined} />
        <span data-testid={DataTestId.userName}>{details?.name?.firstname}</span>
      </div>

      {!isResearcher ? (
        <>
          <AnchorButton variant="dropdown" width="full" asChild>
            <Link to="/settings/account">{t('sections.settings.title')}</Link>
          </AnchorButton>
          <hr />
        </>
      ) : null}

      {hasTeams ? (
        <div>
          <div className="p-3 text-sm font-normal text-muted-foreground">{t('components.teamSwitcher.switchTeam')}</div>
          <div className="mb-2 flex max-h-[40vh] flex-col overflow-y-auto">
            {teams.map(team => {
              const isActiveTeam = isResearcher
                ? team._id === details?.team?.id
                : team._id === details?.ConnectedTeam?._id;

              return (
                <Button
                  key={team._id}
                  variant="dropdown"
                  width="full"
                  onClick={() => void handleSwitchTeams(team._id)}
                  className={cn('py-3', {
                    'bg-secondary font-medium': isActiveTeam,
                  })}
                >
                  <div className="w-4 shrink-0">{isActiveTeam ? <Check className="size-4" /> : null}</div>
                  {team.name}
                </Button>
              );
            })}
          </div>
          {!hideSignIntoAnotherTeam && !isResearcher ? (
            <AnchorButton variant="dropdown" width="full" asChild>
              <Link to="/teams">
                <Users className="size-4" />
                {t('components.teamSwitcher.signInToAnotherTeam')}
              </Link>
            </AnchorButton>
          ) : null}
        </div>
      ) : null}

      {!isResearcher ? (
        <>
          <Button
            variant="dropdown"
            width="full"
            disabled={isGuest}
            onClick={() => {
              setIsOpen(false);
              appContext.onOpenInviteTeamMemberModal(null);
            }}
          >
            {t('sections.navigation.invite')}
          </Button>
          <hr />
        </>
      ) : null}

      {globalVariables.getEnvironmentVariables().isDemo && (
        <Button variant="dropdown" width="full" onClick={() => navigate('/reset-demos')}>
          {t('sections.navigation.resetDemoData')}
        </Button>
      )}

      <Button variant="dropdown" width="full" onClick={handleSignOut}>
        {t('global.signOut')}
      </Button>
    </div>
  );

  return (
    <div
      className={cn(
        'flex flex-1 items-center truncate rounded-sm transition-all focus-within:bg-foreground/[0.04] hover:bg-foreground/[0.04]',
        {
          'bg-foreground/[0.065]': isOpen,
        },
      )}
    >
      <Link to="/studies" className="aspect-square shrink-0 px-2 py-1 pr-0">
        <AskableIcon className="h-5 w-5 text-brand" />
      </Link>
      <ResponsiveMenu
        id="team-selector-dropdown"
        title={t('sections.settings.account.title')}
        isOpen={isOpen}
        onOpenChange={setIsOpen}
        trigger={
          <button
            type="button"
            title={details?.team?.name ?? undefined}
            className={cn(
              `flex h-7 flex-1 items-center overflow-hidden rounded-sm p-2 text-start opacity-100 outline-none transition-all focus:outline-none starting:opacity-0`,
              {
                'text-destructive': connectedAsClient,
              },
            )}
          >
            <div className="flex-1 truncate text-sm font-semibold">{details?.team?.name}</div>
            <ChevronDown className="size-4" />
          </button>
        }
        align="start"
        side="bottom"
        sideOffset={2}
        contentClassName="w-60 sm:w-60 -ml-7"
      >
        {isMounted && isMobile ? mobileContent : desktopContent}
      </ResponsiveMenu>
    </div>
  );
};
